import { Button, Flex, Input, Modal, Space, Tooltip, Typography } from 'antd';
import api from 'api';
import Assets from 'assets/icons';
import DetailsComponent from 'components/DetailsComponent/DetailsComponent';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import { getTheme } from 'helpers/getTheme';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import EmptyRecycleBin from 'routes/RecycleBin/EmptyRecycleBin/EmptyRecycleBin';
import RecycleBinTable from 'routes/RecycleBin/RecycleBinTable/RecycleBinTable';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { fetchRecycleBinItems, forceDeleteRecycleBinItems, restoreRecycleBinItems, restoreRecycleBinItemsInRoot } from 'store/reducers/recycleBinCreator';
import { setSelectedKeys } from 'store/slices/windowStateSlice';
import classes from './RecycleBin.module.scss';

const { Title } = Typography;

export default function RecycleBin() {
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = React.useState(false);
  const [isRestoreToRoot, setIsRestoreToRoot] = React.useState(false);
  const [isCheckingConflicts, setIsCheckingConflicts] = React.useState(false);
  const [isNameConflictModalOpen, setIsNameConflictModalOpen] = React.useState(false);
  const [conflictItems, setConflictItems] = React.useState<{ id: string; name: string; generatedName: string }[]>([]);

  const { dataRoom, isLoadingRoom } = useAppSelector((store) => store.dataRoom);
  const { messageApi } = useAppSelector((store) => store.windowState);
  const { available_rooms } = useAppSelector((store) => store.userData);
  const { items, isLoading, isDeleting, isRestoring } = useAppSelector((store) => store.recycleBin);
  const { viewType } = useAppSelector((store) => store.documents);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();

  const roomId = dataRoom?.id;

  const isOwnerOrFullAdmin = useMemo(
    () => available_rooms.some(room => room.id === roomId && room.is_admin),
    [available_rooms, roomId]
  );

  useEffect(() => {
    dispatch(setSelectedKeys(['recycleBin']));

    if (isLoadingRoom || !roomId) {
      return;
    }

    if (!isOwnerOrFullAdmin) {
      messageApi?.error(t('RecycleBin.noAccess'));
      navigate(`/room/${params.room}/documents`);
      return;
    }

    dispatch(fetchRecycleBinItems({ roomId }));
  }, [roomId, isLoadingRoom, isOwnerOrFullAdmin, dispatch, navigate, params.room, t, messageApi]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase().trim();
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [items, searchQuery]);

  const handleDelete = async () => {
    if (!dataRoom || selectedRowKeys.length === 0) return;

    try {
      await dispatch(
        forceDeleteRecycleBinItems({
          roomId: dataRoom.id,
          selectedIds: selectedRowKeys.map((key) => String(key)),
        })
      )

      messageApi?.success(t('RecycleBin.notifications.deleteSuccess'));
      setSelectedRowKeys([]);
    } catch {
      messageApi?.error(t('RecycleBin.notifications.deleteError'));
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const checkNameConflicts = async (): Promise<{ id: string; name: string; generatedName: string }[]> => {
    if (!dataRoom) return [];
    const selectedFiles = items.filter(
      (item) => selectedRowKeys.includes(item.id) && item.type === 'file'
    );
    const results = await Promise.all(
      selectedFiles.map((item) =>
        api.validateDocuments({ document_name: item.name, room_id: dataRoom.id }).then((res) =>
          res.data.documents.length > 0
            ? { id: item.id, name: item.name, generatedName: res.data.generated_name }
            : null
        )
      )
    );
    return results.filter(Boolean) as { id: string; name: string; generatedName: string }[] | [];
  };

  const performRestore = async (opts?: { silentSuccess?: boolean }) => {
    if (!dataRoom) return;
    try {
      const thunk = isRestoreToRoot ? restoreRecycleBinItemsInRoot : restoreRecycleBinItems;
      await dispatch(
        thunk({
          roomId: dataRoom.id,
          selectedIds: selectedRowKeys.map((key) => String(key)),
        })
      );
      if (!opts?.silentSuccess) {
        messageApi?.success(t('RecycleBin.notifications.recoverSuccess'));
      }
      setSelectedRowKeys([]);
    } catch {
      messageApi?.error(t('RecycleBin.notifications.recoverError'));
    }
  };

  const handleRestore = async () => {
    if (!dataRoom || selectedRowKeys.length === 0) return;
    setIsRestoreModalOpen(false);
    setIsCheckingConflicts(true);
    try {
      const conflicts = await checkNameConflicts();
      if (conflicts.length > 0) {
        setConflictItems(conflicts);
        setIsNameConflictModalOpen(true);
        return;
      }
      await performRestore();
    } catch {
      messageApi?.error(t('RecycleBin.notifications.recoverError'));
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  const handleRenameAndRestore = async () => {
    if (!dataRoom) return;
    setIsNameConflictModalOpen(false);
    setIsCheckingConflicts(true);
    try {
      await Promise.all(
        conflictItems.map((conflict) =>
          api.renameDocument({ id: conflict.id, name: conflict.generatedName })
        )
      );
      await performRestore({ silentSuccess: true });
      messageApi?.success(
        t('RecycleBin.notifications.recoverRenamedSuccess', {
          names: conflictItems.map((c) => c.generatedName).join(', '),
        })
      );
      setConflictItems([]);
    } catch {
      messageApi?.error(t('RecycleBin.notifications.recoverError'));
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  if (viewType === 'Document') {
    return <DetailsComponent />;
  }

  return (
    <Flex vertical className={classes.wrapper}>
      <Flex justify='space-between' align='center' className={classes.header}>
        <Flex align='center' gap={16}>
          <Title level={4} className={classes.title}>
            {t('RecycleBin.title')}
          </Title>
          <Input
            className={classes.searchInput}
            placeholder={t('RecycleBin.searchPlaceholder')}
            suffix={<Assets.SearchIcon className={classes.searchIcon} />}
            type='search'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
          />
        </Flex>
        <Space>
          <Button
            danger
            disabled={selectedRowKeys.length === 0 || isDeleting}
            loading={isDeleting}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            {t('RecycleBin.buttons.delete')}
          </Button>
          <Tooltip
            title={selectedRowKeys.length > 0
              ? t('RecycleBin.tooltips.parentFolderUnavailable')
              : ''}
          >
            <Button
              disabled={selectedRowKeys.length === 0 || isCheckingConflicts || isRestoring}
              loading={(isCheckingConflicts || isRestoring) && !isRestoreToRoot}
              onClick={() => { setIsRestoreToRoot(false); setIsRestoreModalOpen(true); }}
            >
              {t('RecycleBin.buttons.recover')}
            </Button>
          </Tooltip>
          <Button
            type='primary'
            disabled={selectedRowKeys.length === 0 || isCheckingConflicts || isRestoring}
            loading={(isCheckingConflicts || isRestoring) && isRestoreToRoot}
            onClick={() => { setIsRestoreToRoot(true); setIsRestoreModalOpen(true); }}
          >
            {t('RecycleBin.buttons.recoverToRoot')}
          </Button>
        </Space>
      </Flex>

      <Flex vertical flex={1} className={classes.content}>
        {!isLoading && items.length === 0 ? (
          <EmptyRecycleBin />
        ) : (
          <RecycleBinTable
            data={filteredItems}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={(keys) => setSelectedRowKeys(keys)}
            isLoading={isLoading}
            isDeleting={isDeleting}
          />
        )}
      </Flex>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        submit={handleDelete}
        isLoading={isDeleting}
        okText={t('RecycleBin.deleteModal.delete')}
        cancelText={t('RecycleBin.deleteModal.cancel')}
        title={t('RecycleBin.deleteModal.title')}
        text={t('RecycleBin.deleteModal.text')}
        centered
      />

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onCancel={() => setIsRestoreModalOpen(false)}
        submit={handleRestore}
        isLoading={false}
        okText={t(isRestoreToRoot ? 'RecycleBin.recoverToRootModal.confirm' : 'RecycleBin.recoverModal.confirm')}
        cancelText={t(isRestoreToRoot ? 'RecycleBin.recoverToRootModal.cancel' : 'RecycleBin.recoverModal.cancel')}
        title={t(isRestoreToRoot ? 'RecycleBin.recoverToRootModal.title' : 'RecycleBin.recoverModal.title')}
        text={t(isRestoreToRoot ? 'RecycleBin.recoverToRootModal.text' : 'RecycleBin.recoverModal.text')}
        centered
      />
      <Modal
        open={isNameConflictModalOpen}
        title={t('RecycleBin.nameConflictModal.title')}
        onCancel={() => setIsNameConflictModalOpen(false)}
        okText={t('RecycleBin.nameConflictModal.rename')}
        cancelText={t('RecycleBin.nameConflictModal.cancel')}
        onOk={handleRenameAndRestore}
        okButtonProps={{ type: 'primary' }}
        centered
        className={getTheme()}
      >
        <p>{t('RecycleBin.nameConflictModal.text')}</p>
        {conflictItems.length > 0 && (
          <p><strong>{conflictItems.map((c) => c.name).join(', ')}</strong></p>
        )}
      </Modal>
    </Flex>
  );
}
